import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';
import './Toast.css';

// Toast Container - renders all toasts
let toastContainer = null;
let toasts = [];
let setToastsState = null;

function ToastContainer() {
    const [toastList, setToastList] = useState([]);

    useEffect(() => {
        setToastsState = setToastList;
        return () => {
            setToastsState = null;
        };
    }, []);

    const removeToast = useCallback((id) => {
        setToastList((prev) => prev.filter((t) => t.id !== id));
    }, []);

    if (toastList.length === 0) return null;

    return createPortal(
        <div className="toast-container">
            {toastList.map((toast) => (
                <ToastItem key={toast.id} {...toast} onClose={() => removeToast(toast.id)} />
            ))}
        </div>,
        document.body
    );
}

// Individual Toast Item
function ToastItem({ id, type, title, message, duration = 5000, onClose }) {
    useEffect(() => {
        if (duration > 0) {
            const timer = setTimeout(onClose, duration);
            return () => clearTimeout(timer);
        }
    }, [duration, onClose]);

    const icons = {
        success: CheckCircle,
        error: XCircle,
        warning: AlertTriangle,
        info: Info
    };

    const Icon = icons[type] || Info;

    return (
        <div className={`toast toast-${type}`}>
            <div className="toast-icon">
                <Icon size={20} />
            </div>
            <div className="toast-content">
                {title && <div className="toast-title">{title}</div>}
                <div className="toast-message">{message}</div>
            </div>
            <button className="toast-close" onClick={onClose}>
                <X size={16} />
            </button>
            {duration > 0 && (
                <div className="toast-progress">
                    <div
                        className="toast-progress-bar"
                        style={{ animationDuration: `${duration}ms` }}
                    />
                </div>
            )}
        </div>
    );
}

// Toast API
let toastId = 0;

export const toast = {
    show: (options) => {
        const id = ++toastId;
        const newToast = { id, ...options };
        if (setToastsState) {
            setToastsState((prev) => [...prev, newToast]);
        }
        return id;
    },
    success: (message, options = {}) => toast.show({ type: 'success', message, ...options }),
    error: (message, options = {}) => toast.show({ type: 'error', message, ...options }),
    warning: (message, options = {}) => toast.show({ type: 'warning', message, ...options }),
    info: (message, options = {}) => toast.show({ type: 'info', message, ...options }),
    dismiss: (id) => {
        if (setToastsState) {
            setToastsState((prev) => prev.filter((t) => t.id !== id));
        }
    },
    dismissAll: () => {
        if (setToastsState) {
            setToastsState([]);
        }
    }
};

ToastItem.propTypes = {
    id: PropTypes.number,
    type: PropTypes.oneOf(['success', 'error', 'warning', 'info']),
    title: PropTypes.string,
    message: PropTypes.string.isRequired,
    duration: PropTypes.number,
    onClose: PropTypes.func
};

export default ToastContainer;
